export type PadControl =
  | "START"
  | "SELECT"
  | "DPAD_LEFT"
  | "DPAD_RIGHT"
  | "DPAD_UP"
  | "DPAD_DOWN"
  | "SQUARE"
  | "CROSS"
  | "TRIANGLE"
  | "CIRCLE"
  | "L1"
  | "L2"
  | "L3"
  | "R1"
  | "R2"
  | "R3"
  | "LEFT_STICK_LEFT"
  | "LEFT_STICK_RIGHT"
  | "LEFT_STICK_UP"
  | "LEFT_STICK_DOWN"
  | "RIGHT_STICK_LEFT"
  | "RIGHT_STICK_RIGHT"
  | "RIGHT_STICK_UP"
  | "RIGHT_STICK_DOWN";

export interface TextureBind {
  raw: string;
  tbp0: number;
  tbw: number;
  psm: number;
  width: number;
  height: number;
  cbp: number;
}

export interface TextureScan {
  binds: TextureBind[];
  characterBankCandidates: TextureBind[];
}

export interface TextureUpload {
  raw: string;
  dbp: number;
  dbw: number;
  dpsm: number;
  width: number;
  height: number;
  fingerprint: number;
}

export interface UploadScan {
  uploads: TextureUpload[];
  characterBankCandidates: TextureUpload[];
}

export interface PatchedPlayModule {
  setPadControl(control: string, pressed: boolean): boolean;
  pressPad(control: string): boolean;
  releasePad(control: string): boolean;

  getExecutableName(): string;
  getEeRamSize(): number;

  readEe8(address: number): number;
  readEe16(address: number): number;
  readEe32(address: number): number;
  readEeF32(address: number): number;

  writeEe8(address: number, value: number): boolean;
  writeEe16(address: number, value: number): boolean;
  writeEe32(address: number, value: number): boolean;
  writeEeF32(address: number, value: number): boolean;

  beginTextureTrace(): boolean;
  endTextureTrace(): string;
  beginUploadTrace(): boolean;
  endUploadTrace(): string;
}

const sleep = (milliseconds: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, milliseconds));

const decodeTraceRecord = (record: string): TextureBind => {
  const [tbpHex, widthText, heightText, psmText, cbpHex, tbwText] = record.split(":");
  if (!tbpHex || !widthText || !heightText || !psmText || !cbpHex || !tbwText) {
    throw new Error(`Malformed Play! texture trace record: ${record}`);
  }
  return {
    raw: record,
    tbp0: parseInt(tbpHex, 16),
    width: parseInt(widthText, 10),
    height: parseInt(heightText, 10),
    psm: parseInt(psmText, 10),
    cbp: parseInt(cbpHex, 16),
    tbw: parseInt(tbwText, 10),
  };
};

const decodeUploadRecord = (record: string): TextureUpload => {
  const [dbpHex, widthText, heightText, dpsmText, dbwText, fingerprintHex] = record.split(":");
  if (!dbpHex || !widthText || !heightText || !dpsmText || !dbwText || !fingerprintHex) {
    throw new Error(`Malformed Play! upload trace record: ${record}`);
  }
  return {
    raw: record,
    dbp: parseInt(dbpHex, 16),
    width: parseInt(widthText, 10),
    height: parseInt(heightText, 10),
    dpsm: parseInt(dpsmText, 10),
    dbw: parseInt(dbwText, 10),
    fingerprint: parseInt(fingerprintHex, 16),
  };
};

/**
 * Thin semantic wrapper around the patched Play! browser module.
 *
 * It intentionally does not contain game-specific AI. This layer only turns
 * controller actions, memory reads, and short graphics traces into stable
 * primitives that a separate Genie controller can use.
 */
export class GeniePlayBridge {
  constructor(private readonly play: PatchedPlayModule) {}

  executableName(): string {
    return this.play.getExecutableName();
  }

  eeRamSize(): number {
    return this.play.getEeRamSize();
  }

  down(control: PadControl): void {
    if (!this.play.pressPad(control)) {
      throw new Error(`Play! rejected virtual control: ${control}`);
    }
  }

  up(control: PadControl): void {
    if (!this.play.releasePad(control)) {
      throw new Error(`Play! rejected virtual control: ${control}`);
    }
  }

  async hold(control: PadControl, milliseconds: number): Promise<void> {
    this.down(control);
    try {
      await sleep(milliseconds);
    } finally {
      this.up(control);
    }
  }

  async tap(control: PadControl, milliseconds = 80): Promise<void> {
    await this.hold(control, milliseconds);
  }

  async walkForward(milliseconds = 750): Promise<void> {
    await this.hold("LEFT_STICK_UP", milliseconds);
  }

  async walkBackward(milliseconds = 500): Promise<void> {
    await this.hold("LEFT_STICK_DOWN", milliseconds);
  }

  async strafeLeft(milliseconds = 350): Promise<void> {
    await this.hold("LEFT_STICK_LEFT", milliseconds);
  }

  async strafeRight(milliseconds = 350): Promise<void> {
    await this.hold("LEFT_STICK_RIGHT", milliseconds);
  }

  async turnCameraLeft(milliseconds = 250): Promise<void> {
    await this.hold("RIGHT_STICK_LEFT", milliseconds);
  }

  async turnCameraRight(milliseconds = 250): Promise<void> {
    await this.hold("RIGHT_STICK_RIGHT", milliseconds);
  }

  read8(address: number): number {
    return this.play.readEe8(address) >>> 0;
  }

  read16(address: number): number {
    return this.play.readEe16(address) >>> 0;
  }

  read32(address: number): number {
    return this.play.readEe32(address) >>> 0;
  }

  readF32(address: number): number {
    return this.play.readEeF32(address);
  }

  write8(address: number, value: number): void {
    if (!this.play.writeEe8(address, value >>> 0)) {
      throw new RangeError(`Invalid EE write at 0x${address.toString(16)}`);
    }
  }

  write16(address: number, value: number): void {
    if (!this.play.writeEe16(address, value >>> 0)) {
      throw new RangeError(`Invalid EE write at 0x${address.toString(16)}`);
    }
  }

  write32(address: number, value: number): void {
    if (!this.play.writeEe32(address, value >>> 0)) {
      throw new RangeError(`Invalid EE write at 0x${address.toString(16)}`);
    }
  }

  writeF32(address: number, value: number): void {
    if (!this.play.writeEeF32(address, value)) {
      throw new RangeError(`Invalid EE write at 0x${address.toString(16)}`);
    }
  }

  /**
   * Capture every unique TEX0 texture binding that the PS2 Graphics Synthesizer
   * receives during a short window. Offline PACKAGE.BIN analysis verifies MM2's
   * entry-2 character bank uses TBP0 0x3000..0x3665 inclusive, so the filtered
   * set covers the full known character/customization VRAM range.
   */
  async scanTextureBinds(milliseconds = 900): Promise<TextureScan> {
    if (!this.play.beginTextureTrace()) {
      throw new Error("Play! could not start the GS texture tracer");
    }

    await sleep(milliseconds);
    const encoded = this.play.endTextureTrace();
    const binds = encoded
      .split(";")
      .map((item) => item.trim())
      .filter(Boolean)
      .map(decodeTraceRecord);

    const characterBankCandidates = binds.filter(
      (bind) => bind.tbp0 >= 0x3000 && bind.tbp0 <= 0x3665,
    );

    return { binds, characterBankCandidates };
  }

  /**
   * Capture every host-to-local GS upload event during a short window, including
   * repeated writes to one destination and a compact fingerprint of the actual
   * payload bytes. This can distinguish player-part swaps that keep the same
   * VRAM address, dimensions, and transfer count without guessing a disc offset.
   */
  async scanTextureUploads(milliseconds = 900): Promise<UploadScan> {
    if (!this.play.beginUploadTrace()) {
      throw new Error("Play! could not start the GS upload tracer");
    }

    await sleep(milliseconds);
    const encoded = this.play.endUploadTrace();
    const uploads = encoded
      .split(";")
      .map((item) => item.trim())
      .filter(Boolean)
      .map(decodeUploadRecord);

    const characterBankCandidates = uploads.filter(
      (upload) => upload.dbp >= 0x3000 && upload.dbp <= 0x3665,
    );

    return { uploads, characterBankCandidates };
  }

  /**
   * Returns true only for the known Japanese Metropolismania 2 executable.
   * Play! may report the executable using a CD path, underscore, dot, or other
   * serial formatting, so normalize before comparing.
   */
  isJapaneseMm2(): boolean {
    const normalized = this.executableName()
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "");
    return normalized.includes("SLPS20469");
  }

  /**
   * Known Japanese-build research helper.
   *
   * The old X-Terminator code places item 0x0D (Debug Mode) into the final
   * menu-item slot at EE address 0x00227F68. Do not call this on another build.
   */
  installJapaneseDebugMenuItem(): void {
    if (!this.isJapaneseMm2()) {
      throw new Error(
        `Refusing Japanese MM2 debug write for executable: ${this.executableName()}`,
      );
    }
    this.write32(0x00227f68, 0x0000000d);
  }
}
